import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { NotificationEventDef } from "~/api/notificationEvents";
import {
  NOTIFICATION_EVENTS,
  NOTIFICATION_SECTIONS,
  resolveChannel,
} from "~/api/notificationEvents";
import { useApi } from "~/api/provider";
import {
  useCurrentWorkspace,
  useNotificationPreferences,
  useSetNotificationChannel,
} from "~/api/queries";
import { ABILITY } from "~/api/types";
import { Spinner } from "~/foundation/components/Spinner";
import { Switch } from "~/foundation/components/Switch";
import { Text } from "~/foundation/components/Text";

/**
 * W9 §A.5.1 — replaces the pre-existing section wholesale.
 *
 * The file it replaces was a fabrication: four event ids that do not exist server-side
 * (`document.mention`, `document.shared`, `collection.created`, `digest.weekly`), held in
 * `useState`, saved nowhere, with the mention event's email switch defaulting to `true` — the
 * exact thing the estate's email-opt-in rule forbids. This reads and writes the real 28-event
 * taxonomy (`notificationEvents.ts`, mirrored from `CommunicationEventTypes.cs`) through
 * `users.notificationsChannel`, the one route that can turn email on for a single event.
 */
function EventRow({
  event,
  isAdmin,
  telegram,
}: {
  event: NotificationEventDef;
  isAdmin: boolean;
  /** Output channels (2026-10-17): the chat channel = the person's linked Telegram; shown once the bot exists on the host. */
  telegram: { configured: boolean; linked: boolean } | null;
}) {
  const { t } = useTranslation();
  const { data: preferences } = useNotificationPreferences();
  const setChannel = useSetNotificationChannel();

  if (event.adminOnly && !isAdmin) {
    return null;
  }

  const appOn = resolveChannel(preferences, event.id, "app");
  const emailOn = resolveChannel(preferences, event.id, "email");
  const chatOn = resolveChannel(preferences, event.id, "chat");

  return (
    <div className="rounded-md border border-divider px-4">
      <Switch
        checked={appOn}
        onCheckedChange={(value) =>
          setChannel.mutate({ eventType: event.id, channel: "app", enabled: value })
        }
        label={t(event.label)}
        description={t(event.description)}
      />
      <div className="border-t border-divider">
        <Switch
          checked={emailOn}
          onCheckedChange={(value) =>
            setChannel.mutate({ eventType: event.id, channel: "email", enabled: value })
          }
          label={t("Also send an email")}
        />
      </div>
      {telegram?.configured ? (
        <div className="border-t border-divider">
          <Switch
            checked={chatOn}
            onCheckedChange={(value) =>
              setChannel.mutate({ eventType: event.id, channel: "chat", enabled: value })
            }
            label={t("Also send to Telegram")}
            description={
              telegram.linked
                ? undefined
                : t("Your Telegram chat is not linked yet — link it under Settings → Telegram.")
            }
          />
        </div>
      ) : null}
      {event.cloudOnly ? (
        <Text variant="small" className="pb-2">
          {t("Cloud workspaces only.")}
        </Text>
      ) : null}
    </div>
  );
}

export function Notifications() {
  const { t } = useTranslation();
  const api = useApi();
  const telegram = useQuery({
    queryKey: ["work", "telegram", "status"],
    queryFn: async () =>
      (await api.research.post("work.telegram.status", {})) as {
        configured: boolean;
        linked: boolean;
      },
    retry: false,
  });
  const { data: preferences, isLoading } = useNotificationPreferences();
  const { data: workspace } = useCurrentWorkspace();
  const isAdmin = workspace?.abilities[ABILITY.workspaceUpdate] === true;

  if (isLoading || !preferences) {
    return <Spinner label={t("Loading")} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <Text variant="small">
        {t(
          "Email is off for everything until you turn it on for that event — nothing here sends email by default.",
        )}
      </Text>
      <Text variant="small">
        {t("Invitations, sign-in links and confirmation codes always arrive by email.")}
      </Text>

      {NOTIFICATION_SECTIONS.map((section) => {
        const events = NOTIFICATION_EVENTS.filter((event) => event.section === section.id);
        const visible = events.filter((event) => !event.adminOnly || isAdmin);
        if (visible.length === 0) {
          return null;
        }
        return (
          <div key={section.id} className="flex flex-col gap-3">
            <Text as="h2" variant="h3">
              {t(section.label)}
            </Text>
            <div className="flex flex-col gap-3">
              {events.map((event) => (
                <EventRow
                  telegram={telegram.data ?? null}
                  key={event.id}
                  event={event}
                  isAdmin={isAdmin}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
